import { parseAppSearch, type ParsedAppQuery } from './parseAppQuery';

export type AppLocation = {
  pathname: string;
  search: string;
  query: ParsedAppQuery;
  locationVersion: number;
};

const listeners = new Set<() => void>();

function readWindowLocation(): Omit<AppLocation, 'locationVersion'> {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '', query: {} };
  }
  const pathname = window.location.pathname;
  const search = window.location.search || '';
  return {
    pathname,
    search,
    query: parseAppSearch(search),
  };
}

let snapshot: AppLocation = {
  ...readWindowLocation(),
  locationVersion: 0,
};

export function subscribeRouter(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getRouterSnapshot(): AppLocation {
  return snapshot;
}

export function getRouterServerSnapshot(): AppLocation {
  return {
    pathname: '/',
    search: '',
    query: {},
    locationVersion: 0,
  };
}

export function getRouterPathnameSnapshot(): string {
  return snapshot.pathname;
}

export function getRouterSearchSnapshot(): string {
  return snapshot.search;
}

/** Single publish after history + URL are aligned (Mode B). */
export function emitLocationOnce(next: { pathname: string; search: string }): void {
  snapshot = {
    pathname: next.pathname,
    search: next.search,
    query: parseAppSearch(next.search),
    locationVersion: snapshot.locationVersion + 1,
  };
  listeners.forEach((l) => {
    l();
  });
}

export function syncRouterFromWindow(): void {
  const { pathname, search } = readWindowLocation();
  emitLocationOnce({ pathname, search: search || '' });
}
